// src/pages/UserProfilePage.jsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
  updateDoc,
  arrayUnion,
  arrayRemove,
  setDoc,
  getDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  getFollowers,
  getFollowing,
  followUser,
  unfollowUser,
  isFollowing,
} from "../utils/followUtils";
import UserCardShareable from "../components/UserCardShareable";
import { motion, AnimatePresence } from "framer-motion";
import { FaWhatsapp, FaInstagram, FaEnvelope } from "react-icons/fa";
import Avatar from "../components/Avatar";

function UserProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = localStorage.getItem("userId");
  const username = localStorage.getItem("username") || "Misafir";
  const [user, setUser] = useState(null);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [isFollowingUser, setIsFollowingUser] = useState(false);
  const [userPosts, setUserPosts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState("");
  const [visiblePosts, setVisiblePosts] = useState(5);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState("suggestion");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const ref = doc(db, "users", id);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setUser(snap.data());
        } else {
          setError("Kullanıcı bulunamadı.");
        }

        const f1 = await getFollowers(id);
        const f2 = await getFollowing(id);
        setFollowers(f1);
        setFollowing(f2);

        const isF = await isFollowing(currentUser, id);
        setIsFollowingUser(isF);
      } catch (err) {
        setError(`Profil yüklenirken hata oluştu: ${err.message}`);
      }
    };

    const fetchUserPosts = async () => {
      try {
        const q = query(
          collection(db, "forumPosts"),
          where("userId", "==", id),
          orderBy("timestamp", "desc")
        );
        const snapshot = await getDocs(q);
        const data = await Promise.all(
          snapshot.docs.map(async (d) => {
            const postData = d.data();
            return {
              id: d.id,
              ...postData,
              likedBy: postData.likedBy || [],
              likes: postData.likedBy?.length || 0,
              dislikedBy: postData.dislikedBy || [],
              dislikes: postData.dislikedBy?.length || 0,
              savedBy: postData.savedBy || [],
              comments: postData.comments || [],
              createdAt: postData.createdAt || postData.timestamp || new Date(),
            };
          })
        );
        setUserPosts(data);
      } catch (err) {
        setError(`Gönderiler yüklenirken hata oluştu: ${err.message}`);
      }
    };

    if (id) {
      fetchProfile();
      fetchUserPosts();
    }
  }, [id, currentUser]);

  const handleFollow = async () => {
    if (!currentUser || !id || currentUser === id) return;
    try {
      if (isFollowingUser) {
        await unfollowUser(currentUser, id);
      } else {
        await followUser(currentUser, id);
      }
      const updated = await isFollowing(currentUser, id);
      setIsFollowingUser(updated);
      const updatedFollowers = await getFollowers(id);
      setFollowers(updatedFollowers);
    } catch (err) {
      setError(`Takip işlemi sırasında hata oluştu: ${err.message}`);
    }
  };

  const handleSendMessage = async () => {
    if (!currentUser || !id || currentUser === id) return;
    try {
      const chatId = [currentUser, id].sort().join("_");
      const chatRef = doc(db, "chats", chatId);
      const chatSnap = await getDoc(chatRef);

      if (!chatSnap.exists()) {
        await setDoc(chatRef, {
          users: [currentUser, id],
          lastMessage: "",
          timestamp: serverTimestamp(),
        });
      }

      navigate(`/chat/${id}`);
    } catch (err) {
      setError(`Mesajlaşma başlatılırken hata oluştu: ${err.message}`);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!title.trim() || !description.trim()) {
      setError("Lütfen tüm alanları doldurun.");
      return;
    }

    try {
      const feedbackData = {
        userId: currentUser || "anonymous",
        username,
        type: feedbackType,
        title: title.trim(),
        description: description.trim(),
        timestamp: new Date(),
        status: "pending",
      };

      await addDoc(collection(db, "feedbacks"), feedbackData);

      setSuccess(true);
      setTitle("");
      setDescription("");
      setFeedbackType("suggestion");

      setTimeout(() => {
        setSuccess(false);
        setIsFeedbackModalOpen(false);
      }, 3000);
    } catch (err) {
      setError(`Geri bildirim gönderilirken hata oluştu: ${err.message}`);
    }
  };

  const toggleLike = async (postId) => {
    try {
      const post = userPosts.find((p) => p.id === postId);
      const postRef = doc(db, "forumPosts", postId);
      const isLiked = post.likedBy?.includes(currentUser);

      if (isLiked) {
        await updateDoc(postRef, { likedBy: arrayRemove(currentUser) });
        setUserPosts(userPosts.map(p => p.id === postId ? { ...p, likedBy: p.likedBy.filter(id => id !== currentUser), likes: p.likes - 1 } : p));
      } else {
        await updateDoc(postRef, { likedBy: arrayUnion(currentUser), dislikedBy: arrayRemove(currentUser) });
        setUserPosts(userPosts.map(p => p.id === postId ? { 
          ...p, 
          likedBy: [...(p.likedBy || []), currentUser], 
          likes: p.likes + 1,
          dislikedBy: p.dislikedBy?.filter(id => id !== currentUser) || [],
          dislikes: p.dislikedBy?.includes(currentUser) ? p.dislikes - 1 : p.dislikes
        } : p));
      }
    } catch (err) {
      setError(`Beğeni işlemi sırasında hata oluştu: ${err.message}`);
    }
  };

  const toggleDislike = async (postId) => {
    try {
      const post = userPosts.find((p) => p.id === postId);
      const postRef = doc(db, "forumPosts", postId);
      const isDisliked = post.dislikedBy?.includes(currentUser);

      if (isDisliked) {
        await updateDoc(postRef, { dislikedBy: arrayRemove(currentUser) });
        setUserPosts(userPosts.map(p => p.id === postId ? { ...p, dislikedBy: p.dislikedBy.filter(id => id !== currentUser), dislikes: p.dislikes - 1 } : p));
      } else {
        await updateDoc(postRef, { dislikedBy: arrayUnion(currentUser), likedBy: arrayRemove(currentUser) });
        setUserPosts(userPosts.map(p => p.id === postId ? { 
          ...p, 
          dislikedBy: [...(p.dislikedBy || []), currentUser], 
          dislikes: p.dislikes + 1,
          likedBy: p.likedBy?.filter(id => id !== currentUser) || [],
          likes: p.likedBy?.includes(currentUser) ? p.likes - 1 : p.likes
        } : p));
      }
    } catch (err) {
      setError(`Dislike işlemi sırasında hata oluştu: ${err.message}`);
    }
  };

  const toggleSave = async (postId) => {
    try {
      const post = userPosts.find((p) => p.id === postId);
      const postRef = doc(db, "forumPosts", postId);
      const isSaved = post.savedBy?.includes(currentUser);

      if (isSaved) {
        await updateDoc(postRef, { savedBy: arrayRemove(currentUser) });
        setUserPosts(userPosts.map(p => p.id === postId ? { ...p, savedBy: p.savedBy.filter(id => id !== currentUser) } : p));
      } else {
        await updateDoc(postRef, { savedBy: arrayUnion(currentUser) });
        setUserPosts(userPosts.map(p => p.id === postId ? { ...p, savedBy: [...(p.savedBy || []), currentUser] } : p));
      }
    } catch (err) {
      setError(`Kaydetme işlemi sırasında hata oluştu: ${err.message}`);
    }
  };

  const refreshUserPosts = async () => {
    try {
      const q = query(
        collection(db, "forumPosts"),
        where("userId", "==", id),
        orderBy("timestamp", "desc")
      );
      const snapshot = await getDocs(q);
      
      const postIds = snapshot.docs.map(d => d.id);
      let allComments = [];
      
      if (postIds.length > 0) {
        // 'in' sorgusu maksimum 30 öğe alır. Kullanıcı profilinde ilk 30 postun yorumlarını çekiyoruz.
        const chunk = postIds.slice(0, 30);
        try {
          const commentsQuery = query(collection(db, "comments"), where("postId", "in", chunk));
          const commentsSnap = await getDocs(commentsQuery);
          allComments = commentsSnap.docs.map(c => ({ id: c.id, ...c.data() }));
        } catch (err) {
          console.error("Yorumlar çekilirken hata:", err);
        }
      }

      const data = snapshot.docs.map((d) => {
          const postData = d.data();
          const postComments = allComments.filter(c => c.postId === d.id);
          
          return {
            id: d.id,
            ...postData,
            likedBy: postData.likedBy || [],
            likes: postData.likedBy?.length || 0,
            dislikedBy: postData.dislikedBy || [],
            dislikes: postData.dislikedBy?.length || 0,
            savedBy: postData.savedBy || [],
            comments: postComments,
            createdAt: postData.createdAt || postData.timestamp || new Date(),
          };
        });
      setUserPosts(data);
    } catch (err) {
      setError(`Kullanıcı gönderileri yenilenirken hata oluştu: ${err.message}`);
    }
  };

  if (!user) {
    return (
      <motion.div
        style={loadingContainerStyle}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        Yükleniyor...
      </motion.div>
    );
  }

  return (
    <motion.div
      className="main-layout"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="home-content"
        style={containerStyle}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          style={contentWrapperStyle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            style={shareableCardStyle}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <UserCardShareable userId={id} />
          </motion.div>

          <motion.div
            style={profileContainerStyle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <motion.div
              style={profileImageWrapperStyle}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Avatar avatarData={user.avatar || user.profilePhoto} seedFallback={user.username} size={120} />
            </motion.div>
            <motion.h2
              style={usernameStyle}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              @{user.username}
            </motion.h2>
            <motion.p
              style={aboutStyle}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              {user.about || "Hakkında bilgi yok."}
            </motion.p>
            <motion.div
              style={statsContainerStyle}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <motion.div
                style={statItemStyle}
                whileHover={{ scale: 1.05, backgroundColor: "rgba(0, 230, 118, 0.1)" }}
                whileTap={{ scale: 0.95 }}
              >
                <strong style={statNumberStyle}>{followers.length}</strong>
                <p style={statLabelStyle}>Takipçi</p>
              </motion.div>
              <motion.div
                style={statItemStyle}
                whileHover={{ scale: 1.05, backgroundColor: "rgba(0, 230, 118, 0.1)" }}
                whileTap={{ scale: 0.95 }}
              >
                <strong style={statNumberStyle}>{following.length}</strong>
                <p style={statLabelStyle}>Takip</p>
              </motion.div>
            </motion.div>
            {id !== currentUser && (
              <motion.div
                style={actionButtonsContainerStyle}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                <motion.button
                  onClick={handleFollow}
                  style={followButtonStyle(isFollowingUser)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  {isFollowingUser ? "Takibi Bırak" : "Takip Et"}
                </motion.button>
                {user.allowMessages !== false && (
                  <motion.button
                    onClick={handleSendMessage}
                    style={messageButtonStyle}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    Mesaj Gönder
                  </motion.button>
                )}
                <motion.button
                  onClick={() => setIsFeedbackModalOpen(true)}
                  style={feedbackButtonStyle}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  Geri Bildirim Gönder
                </motion.button>
              </motion.div>
            )}
          </motion.div>

          <motion.div
            style={badgesContainerStyle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
          >
            <h3 style={sectionTitleStyle}>🌟 Rozetler</h3>
            {user.badges && user.badges.length > 0 ? (
              <div style={badgeGridStyle}>
                {user.badges.map((b, i) => (
                  <motion.div
                    key={i}
                    style={badgeItemStyle}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8 + i * 0.1, duration: 0.3 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span style={badgeIconStyle}>{b}</span>
                  </motion.div>
                ))}
              </div>
            ) : (
              <p style={emptyTextStyle}>Henüz rozet yok.</p>
            )}
            <p style={examInfoStyle}>
              🎯 Ana Sınav: <strong>{user.examType || "Bilinmiyor"}</strong>
            </p>
          </motion.div>

          <motion.div
            style={postsContainerStyle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.5 }}
          >
            <h3 style={sectionTitleStyle}>📢 Kullanıcının Gönderileri</h3>
            <AnimatePresence>
              {userPosts.slice(0, visiblePosts).length === 0 ? (
                <motion.p
                  style={emptyTextStyle}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  Henüz gönderi yok.
                </motion.p>
              ) : (
                userPosts.slice(0, visiblePosts).map((post, index) => (
                  <motion.div
                    key={post.id}
                    style={postCardStyle}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <p style={postTextStyle}>{post.text}</p>
                    {post.media && (
                      <div style={postMediaContainerStyle}>
                        {post.media.endsWith(".pdf") ? (
                          <a
                            href={post.media}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={pdfLinkStyle}
                          >
                            📄 PDF dosyasını görüntüle
                          </a>
                        ) : (
                          <img
                            src={post.media}
                            alt="görsel"
                            style={postImageStyle}
                          />
                        )}
                      </div>
                    )}
                    <div style={postTimestampStyle}>
                      {post.timestamp && typeof post.timestamp.toDate === "function"
                        ? post.timestamp.toDate().toLocaleString()
                        : "Tarih bilinmiyor"}
                    </div>
                    <div style={postActionsStyle}>
                      <motion.button
                        onClick={() => toggleLike(post.id)}
                        style={likeButtonStyle(post.likedBy?.includes(currentUser))}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                      >
                        {post.likedBy?.includes(currentUser) ? "❤️" : "🤍"} Beğen (
                        {post.likedBy?.length || 0})
                      </motion.button>
                      <motion.button
                        onClick={() => toggleDislike(post.id)}
                        style={dislikeButtonStyle(
                          post.dislikedBy?.includes(currentUser)
                        )}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                      >
                        {post.dislikedBy?.includes(currentUser) ? "👎" : "👎🏻"}{" "}
                        Dislike ({post.dislikedBy?.length || 0})
                      </motion.button>
                      <motion.button
                        onClick={() => toggleSave(post.id)}
                        style={saveButtonStyle(post.savedBy?.includes(currentUser))}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                      >
                        {post.savedBy?.includes(currentUser)
                          ? "📌 Kaydedildi"
                          : "📍 Kaydet"}
                      </motion.button>
                      <motion.button
                        onClick={() => setIsFeedbackModalOpen(true)}
                        style={feedbackButtonStyle}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                      >
                        Geri Bildirim Gönder
                      </motion.button>
                    </div>
                  </motion.div>
                ))
              )}
            </AnimatePresence>
            {visiblePosts < userPosts.length && (
              <motion.button
                style={loadMoreButtonStyle}
                onClick={() => setVisiblePosts((prev) => prev + 5)}
                whileHover={{ scale: 1.05, backgroundColor: "#00c853" }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                Daha Fazla Göster
              </motion.button>
            )}
          </motion.div>

          <AnimatePresence>
            {isFeedbackModalOpen && (
              <motion.div
                className="feedback-modal"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="feedback-modal-content"
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.button
                    className="feedback-modal-close"
                    onClick={() => setIsFeedbackModalOpen(false)}
                    whileHover={{ scale: 1.2, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    ✕
                  </motion.button>

                  <motion.p
                    className="feedback-intro"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    Görüşleriniz bizim için çok değerli! Platformu daha iyi hale getirmek için önerilerinizi, hataları veya genel yorumlarınızı paylaşabilirsiniz.
                  </motion.p>

                  <AnimatePresence>
                    {success && (
                      <motion.div
                        className="feedback-success-message"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3 }}
                      >
                        🎉 Geri bildiriminiz başarıyla gönderildi! Teşekkür ederiz.
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {error && (
                      <motion.div
                        className="feedback-error-message"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3 }}
                      >
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.form
                    onSubmit={handleFeedbackSubmit}
                    className="feedback-form"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                  >
                    <motion.div className="feedback-form-group">
                      <label htmlFor="feedbackType" className="feedback-label">
                        Geri Bildirim Türü:
                      </label>
                      <motion.select
                        id="feedbackType"
                        value={feedbackType}
                        onChange={(e) => setFeedbackType(e.target.value)}
                        className="feedback-select"
                        whileFocus={{ borderColor: "#00e676" }}
                        transition={{ duration: 0.2 }}
                      >
                        <option value="suggestion">Öneri</option>
                        <option value="bug">Hata Bildirimi</option>
                        <option value="general">Genel Yorum</option>
                      </motion.select>
                    </motion.div>

                    <motion.div className="feedback-form-group">
                      <label htmlFor="title" className="feedback-label">
                        Başlık:
                      </label>
                      <motion.input
                        type="text"
                        id="title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Geri bildiriminizin başlığını yazın..."
                        className="feedback-input"
                        whileFocus={{ borderColor: "#00e676" }}
                        transition={{ duration: 0.2 }}
                      />
                    </motion.div>

                    <motion.div className="feedback-form-group">
                      <label htmlFor="description" className="feedback-label">
                        Açıklama:
                      </label>
                      <motion.textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Detaylı açıklamanızı buraya yazın..."
                        className="feedback-textarea"
                        whileFocus={{ borderColor: "#00e676" }}
                        transition={{ duration: 0.2 }}
                      />
                    </motion.div>

                    <motion.button
                      type="submit"
                      className="feedback-submit-button"
                      whileHover={{ scale: 1.05, backgroundColor: "#FFB300" }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      Gönder
                    </motion.button>
                  </motion.form>

                  <motion.div
                    className="feedback-social-icons"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  >
                    <motion.a
                      href="https://wa.me/+905442256334"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="feedback-social-icon whatsapp"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FaWhatsapp />
                    </motion.a>
                    <motion.a
                      href="https://www.instagram.com/yelken.pro/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="feedback-social-icon instagram"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FaInstagram />
                    </motion.a>
                    <motion.a
                      href="mailto:destek@yelken.pro"
                      className="feedback-social-icon gmail"
                      while reproduced in the code
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FaEnvelope />
                    </motion.a>
                  </motion.div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

// Stil Tanımlamaları
const loadingContainerStyle = {
  padding: "40px",
  color: "#f0f0f0",
  fontSize: "18px",
  textAlign: "center",
  background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
  minHeight: "100vh",
  fontFamily: "'Orbitron', sans-serif",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const containerStyle = {
  background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
  minHeight: "100vh",
  padding: "20px",
  fontFamily: "'Orbitron', sans-serif",
  color: "#f1f1f1",
  boxSizing: "border-box",
};

const contentWrapperStyle = {
  maxWidth: "800px",
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const shareableCardStyle = {
  display: "flex",
  justifyContent: "center",
  marginBottom: "20px",
};

const profileContainerStyle = {
  background: "rgba(255, 255, 255, 0.05)",
  borderRadius: "12px",
  padding: "20px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
  border: "1px solid rgba(0, 230, 118, 0.2)",
  textAlign: "center",
};

const profileImageWrapperStyle = {
  width: "120px",
  height: "120px",
  margin: "0 auto 15px",
  borderRadius: "50%",
  overflow: "hidden",
  border: "3px solid #00e676",
  boxShadow: "0 0 10px rgba(0, 230, 118, 0.3)",
  "@media (max-width: 600px)": {
    width: "80px",
    height: "80px",
  },
};

const profileAvatarStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const profilePlaceholderStyle = {
  width: "100%",
  height: "100%",
  background: "#2a2a2a",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "48px",
  color: "#b0bec5",
  "@media (max-width: 600px)": {
    fontSize: "32px",
  },
};

const usernameStyle = {
  fontSize: "28px",
  color: "#00ffcc",
  textShadow: "0 0 10px rgba(0, 230, 118, 0.5)",
  marginBottom: "10px",
  "@media (max-width: 600px)": {
    fontSize: "22px",
  },
};

const aboutStyle = {
  fontSize: "16px",
  color: "#b0bec5",
  marginBottom: "15px",
  "@media (max-width: 600px)": {
    fontSize: "14px",
  },
};

const statsContainerStyle = {
  display: "flex",
  justifyContent: "center",
  gap: "20px",
  marginBENottom: "20px",
  "@media (max-width: 600px)": {
    gap: "10px",
  },
};

const statItemStyle = {
  background: "rgba(255, 255, 255, 0.05)",
  padding: "10px 20px",
  borderRadius: "8px",
};

const statNumberStyle = {
  fontSize: "20px",
  color: "#00e676",
  fontWeight: "bold",
  "@media (max-width: 600px)": {
    fontSize: "18px",
  },
};

const statLabelStyle = {
  fontSize: "14px",
  color: "#b0bec5",
  "@media (max-width: 600px)": {
    fontSize: "12px",
  },
};

const actionButtonsContainerStyle = {
  display: "flex",
  justifyContent: "center",
  gap: "15px",
  flexWrap: "wrap",
};

const followButtonStyle = (isFollowing) => ({
  padding: "10px 20px",
  background: isFollowing
    ? "linear-gradient(135deg, #757575, #616161)"
    : "linear-gradient(135deg, #00e676, #00c853)",
  border: "none",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "14px",
  fontWeight: "bold",
  cursor: "pointer",
  minWidth: "120px",
  "@media (max-width: 600px)": {
    padding: "8px 16px",
    fontSize: "13px",
    minWidth: "100px",
  },
});

const messageButtonStyle = {
  padding: "10px 20px",
  background: "linear-gradient(135deg, #2196f3, #1976d2)",
  border: "none",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "14px",
  fontWeight: "bold",
  cursor: "pointer",
  minWidth: "120px",
  "@media (max-width: 600px)": {
    padding: "8px 16px",
    fontSize: "13px",
    minWidth: "100px",
  },
};

const feedbackButtonStyle = {
  padding: "8px 16px",
  background: "linear-gradient(135deg, #FFCA28, #FFB300)",
  border: "none",
  borderRadius: "8px",
  color: "#000",
  fontSize: "14px",
  fontWeight: "bold",
  cursor: "pointer",
  "@media (max-width: 600px)": {
    padding: "6px 12px",
    fontSize: "13px",
  },
};

const badgesContainerStyle = {
  background: "rgba(255, 255, 255, 0.05)",
  borderRadius: "12px",
  padding: "20px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
  border: "1px solid rgba(0, 230, 118, 0.2)",
  marginTop: "20px",
};

const sectionTitleStyle = {
  fontSize: "22px",
  color: "#00ffcc",
  textShadow: "0 0 10px rgba(0, 230, 118, 0.5)",
  marginBottom: "15px",
  "@media (max-width: 600px)": {
    fontSize: "18px",
  },
};

const badgeGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(60px, 1fr))",
  gap: "10px",
};

const badgeItemStyle = {
  background: "rgba(255, 255, 255, 0.1)",
  borderRadius: "8px",
  padding: "10px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  border: "1px solid rgba(0, 230, 118, 0.2)",
};

const badgeIconStyle = {
  fontSize: "28px",
  "@media (max-width: 600px)": {
    fontSize: "24px",
  },
};

const emptyTextStyle = {
  color: "#b0bec5",
  fontSize: "16px",
  textAlign: "center",
  "@media (max-width: 600px)": {
    fontSize: "14px",
  },
};

const examInfoStyle = {
  color: "#b0bec5",
  fontSize: "16px",
  marginTop: "15px",
  "@media (max-width: 600px)": {
    fontSize: "14px",
  },
};

const postsContainerStyle = {
  marginTop: "20px",
};

const postCardStyle = {
  background: "rgba(255, 255, 255, 0.05)",
  borderRadius: "12px",
  padding: "15px",
  marginBottom: "15px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
  border: "1px solid rgba(0, 230, 118, 0.2)",
};

const postTextStyle = {
  fontSize: "16px",
  color: "#f0f0f0",
  marginBottom: "10px",
  "@media (max-width: 600px)": {
    fontSize: "14px",
  },
};

const postMediaContainerStyle = {
  marginBottom: "10px",
};

const pdfLinkStyle = {
  color: "#00ffcc",
  textDecoration: "underline",
  fontSize: "14px",
  "@media (max-width: 600px)": {
    fontSize: "13px",
  },
};

const postImageStyle = {
  maxWidth: "100%",
  borderRadius: "8px",
};

const postTimestampStyle = {
  fontSize: "12px",
  color: "#b0bec5",
  textAlign: "right",
  marginBottom: "10px",
  "@media (max-width: 600px)": {
    fontSize: "11px",
  },
};

const postActionsStyle = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
};

const likeButtonStyle = (isLiked) => ({
  padding: "8px 16px",
  background: isLiked
    ? "linear-gradient(135deg, #ff4081, #e91e63)"
    : "linear-gradient(135deg, #424242, #212121)",
  border: "none",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "14px",
  cursor: "pointer",
  "@media (max-width: 600px)": {
    padding: "6px 12px",
    fontSize: "13px",
  },
});

const dislikeButtonStyle = (isDisliked) => ({
  padding: "8px 16px",
  background: isDisliked
    ? "linear-gradient(135deg, #757575, #616161)"
    : "linear-gradient(135deg, #424242, #212121)",
  border: "none",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "14px",
  cursor: "pointer",
  "@media (max-width: 600px)": {
    padding: "6px 12px",
    fontSize: "13px",
  },
});

const saveButtonStyle = (isSaved) => ({
  padding: "8px 16px",
  background: isSaved
    ? "linear-gradient(135deg, #00bcd4, #00acc1)"
    : "linear-gradient(135deg, #424242, #212121)",
  border: "none",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "14px",
  cursor: "pointer",
  "@media (max-width: 600px)": {
    padding: "6px 12px",
    fontSize: "13px",
  },
});

const loadMoreButtonStyle = {
  display: "block",
  margin: "20px auto",
  padding: "10px 20px",
  background: "linear-gradient(135deg, #00e676, #00c853)",
  border: "none",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "14px",
  fontWeight: "bold",
  cursor: "pointer",
  "@media (max-width: 600px)": {
    padding: "8px 16px",
    fontSize: "13px",
  },
};

export default UserProfilePage;